import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  HardDrive,
  Database,
  TrendingUp,
  AlertTriangle,
  Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AdminHeader } from '../../../shared/components';
import { adminAuthService } from '../../../shared/services/adminAuth';

interface CapacityData {
  database: {
    totalSize: number;
    totalSizeFormatted: string;
    dataSize: number;
    dataSizeFormatted: string;
    indexSize: number;
    indexSizeFormatted: string;
    collections: Array<{
      name: string;
      size: number;
      sizeFormatted: string;
      documents: number;
      avgDocSize: number;
    }>;
  };
  disk: {
    total: number;
    used: number;
    free: number;
    usagePercentage: number;
    totalFormatted: string;
    usedFormatted: string;
    freeFormatted: string;
  };
  growth: {
    dailyGrowth: number;
    weeklyGrowth: number;
    monthlyGrowth: number;
    dailyGrowthFormatted: string;
    weeklyGrowthFormatted: string;
    monthlyGrowthFormatted: string;
    predictions?: {
      oneMonth: { size: number; sizeFormatted: string; date: string };
      threeMonths: { size: number; sizeFormatted: string; date: string };
      sixMonths: { size: number; sizeFormatted: string; date: string };
      oneYear: { size: number; sizeFormatted: string; date: string };
    };
  };
}

interface CollectionDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  collectionName: string;
}

const CollectionDataModal: React.FC<CollectionDataModalProps> = ({ 
  isOpen, 
  onClose, 
  collectionName 
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const [fieldOrder, setFieldOrder] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && collectionName) {
      fetchCollectionData();
    }
  }, [isOpen, collectionName, page]);

  const getAuthHeaders = () => {
    const token = adminAuthService.getToken();
    if (!token) {
      throw new Error('No admin token available');
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchCollectionData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/admin/capacity/collection/${collectionName}?page=${page}&limit=10`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success) {
        setData(result.data);
        
        // 컬렉션별 필드 순서 정의
        const predefinedOrder = getFieldOrder(collectionName, result.data.data);
        setFieldOrder(predefinedOrder);
      } else {
        throw new Error(result.message || '데이터 조회 실패');
      }
    } catch (error: any) {
      console.error('컬렉션 데이터 조회 실패:', error);
      setError(error.message || '데이터를 불러올 수 없습니다');
    } finally {
      setLoading(false);
    }
  };

  // 컬렉션별 필드 순서를 정의하는 함수
  const getFieldOrder = (collection: string, documents: any[]): string[] => {
    if (!documents || documents.length === 0) {return [];}

    // 컬렉션별 미리 정의된 필드 순서
    const predefinedOrders: { [key: string]: string[] } = {
      urls: [
        '_id', 'originalUrl', 'shortCode', 'isCustom', 'title', 'description', 
        'favicon', 'userId', 'isPublic', 'tags', 'category', 'notes', 
        'folderId', 'isFavorite', 'isActive', 'clickCount', 'qrCode',
        'createdAt', 'updatedAt', '__v', 'sharedWith', 'teamId', 'teamPermissions'
      ],
      users: [
        '_id', 'username', 'email', 'password', 'avatar', 'isVerified', 
        'subscription', 'settings', 'createdAt', 'updatedAt', '__v'
      ],
      clicks: [
        '_id', 'urlId', 'shortCode', 'userAgent', 'ipAddress', 'referer',
        'country', 'city', 'device', 'browser', 'os', 'createdAt', '__v'
      ],
      folders: [
        '_id', 'name', 'description', 'color', 'icon', 'userId', 
        'parentFolder', 'isDefault', 'sortOrder', 'createdAt', 'updatedAt', '__v'
      ],
      tags: [
        '_id', 'name', 'color', 'userId', 'usageCount', 'createdAt', 'updatedAt', '__v'
      ]
    };

    // 미리 정의된 순서가 있으면 사용
    if (predefinedOrders[collection]) {
      const predefined = predefinedOrders[collection];
      const actualFields = Object.keys(documents[0]);
      
      // 정의된 순서대로 정렬하되, 누락된 필드는 뒤에 추가
      const orderedFields = predefined.filter(field => actualFields.includes(field));
      const remainingFields = actualFields.filter(field => !predefined.includes(field));
      
      return [...orderedFields, ...remainingFields];
    }

    // 미리 정의된 순서가 없으면 일반적인 필드 우선순위 적용
    const commonOrder = ['_id', 'name', 'title', 'originalUrl', 'shortCode', 'email', 'username'];
    const actualFields = Object.keys(documents[0]);
    const orderedFields = commonOrder.filter(field => actualFields.includes(field));
    const remainingFields = actualFields.filter(field => !commonOrder.includes(field));
    
    return [...orderedFields, ...remainingFields];
  };

  // 필드명을 사용자 친화적으로 변환하는 함수
  const getFieldDisplayName = (fieldName: string): string => {
    const displayNames: { [key: string]: string } = {
      '_id': 'ID',
      'originalUrl': '원본 URL',
      'shortCode': '단축 코드',
      'isCustom': '커스텀 여부',
      'title': '제목',
      'description': '설명',
      'favicon': '파비콘',
      'userId': '사용자 ID',
      'isPublic': '공개 여부',
      'tags': '태그',
      'category': '카테고리',
      'notes': '메모',
      'folderId': '폴더 ID',
      'isFavorite': '즐겨찾기',
      'isActive': '활성 상태',
      'clickCount': '클릭 수',
      'qrCode': 'QR 코드',
      'createdAt': '생성일',
      'updatedAt': '수정일',
      '__v': '버전',
      'sharedWith': '공유 대상',
      'teamId': '팀 ID',
      'teamPermissions': '팀 권한',
      'username': '사용자명',
      'email': '이메일',
      'password': '비밀번호',
      'avatar': '아바타',
      'isVerified': '인증 여부',
      'subscription': '구독',
      'settings': '설정'
    };

    return displayNames[fieldName] || fieldName;
  };

  // 필드 값을 적절히 포맷팅하는 함수
  const formatFieldValue = (fieldName: string, value: any): string => {
    // null 또는 undefined 처리
    if (value === null || value === undefined) {
      return '-';
    }

    // 불린 값 처리
    if (typeof value === 'boolean') {
      return value ? '✓' : '✗';
    }

    // 날짜 처리
    if (fieldName.includes('At') || fieldName.includes('Date')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      } catch (e) {
        // 날짜 파싱 실패 시 원본 값 표시
      }
    }

    // 배열 처리
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '[]';
      }
      return `[${value.length}개 항목]`;
    }

    // 객체 처리
    if (typeof value === 'object') {
      const jsonStr = JSON.stringify(value, null, 2);
      if (jsonStr.length > 100) {
        return jsonStr.substring(0, 100) + '...';
      }
      return jsonStr;
    }

    // 문자열 길이 제한
    const stringValue = String(value);
    if (stringValue.length > 50) {
      return stringValue.substring(0, 50) + '...';
    }

    return stringValue;
  };

  if (!isOpen) {return null;}

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] m-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {collectionName} 컬렉션 데이터
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        <div className="p-6 max-h-[calc(90vh-8rem)] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-2 text-gray-600 dark:text-gray-400">데이터 로딩 중...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 mb-4">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {data && !loading && (
            <>
              <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                총 {data.totalDocuments.toLocaleString()}개 문서 
                (페이지 {data.pagination.page} / {data.pagination.totalPages})
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      {fieldOrder.map((fieldName) => (
                        <th
                          key={fieldName}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        >
                          {getFieldDisplayName(fieldName)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {data.data.map((item: any, index: number) => (
                      <tr key={item._id || index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        {fieldOrder.map((fieldName) => {
                          const value = item[fieldName];
                          return (
                            <td
                              key={fieldName}
                              className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100"
                            >
                              {formatFieldValue(fieldName, value)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 페이지네이션 */}
              <div className="flex items-center justify-between mt-6">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={!data.pagination.hasPrev}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                >
                  이전 페이지
                </button>
                
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  페이지 {data.pagination.page} / {data.pagination.totalPages}
                </span>

                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={!data.pagination.hasNext}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                >
                  다음 페이지
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const CapacityManagementPage: React.FC = () => {
  const [capacityData, setCapacityData] = useState<CapacityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [clearingCollection, setClearingCollection] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await fetchCapacityData();
    setLastUpdated(new Date());
  };

  const getAuthHeaders = () => {
    const token = adminAuthService.getToken();
    if (!token) {
      throw new Error('No admin token available');
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchCapacityData = async () => {
    try {
      setError('');
      const response = await fetch('/admin/capacity/current', {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setCapacityData({
          database: data.database,
          disk: data.disk,
          growth: data.growth || {
            dailyGrowth: 0,
            weeklyGrowth: 0,
            monthlyGrowth: 0,
            dailyGrowthFormatted: '0 MB/day',
            weeklyGrowthFormatted: '0 MB/week',
            monthlyGrowthFormatted: '0 MB/month'
          }
        });
      } else {
        throw new Error(data.message || '용량 데이터 조회 실패');
      }
    } catch (error: any) {
      console.error('용량 데이터 조회 실패:', error);
      setError('용량 데이터 조회 실패');
      toast.error('용량 데이터를 불러올 수 없습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadData();
      toast.success('데이터가 새로고침되었습니다');
    } catch (error) {
      toast.error('새로고침에 실패했습니다');
    } finally {
      setRefreshing(false);
    }
  };

  const handleClearCollection = async (collectionName: string) => {
    // 보호된 컬렉션 확인
    const protectedCollections = ['users', 'admin_users'];
    if (protectedCollections.includes(collectionName)) {
      toast.error('이 컬렉션은 보호되어 초기화할 수 없습니다');
      return;
    }

    const confirmMessage = `정말로 '${collectionName}' 컬렉션의 모든 데이터를 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setClearingCollection(collectionName);
      
      const response = await fetch(`/admin/capacity/collection/${collectionName}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success(`${collectionName} 컬렉션이 초기화되었습니다 (${result.deletedCount}개 문서 삭제)`);
        // 데이터 새로고침
        await loadData();
      } else {
        throw new Error(result.message || '컬렉션 초기화 실패');
      }
    } catch (error: any) {
      console.error('컬렉션 초기화 실패:', error);
      toast.error(error.message || '컬렉션 초기화에 실패했습니다');
    } finally {
      setClearingCollection(null);
    }
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) {return 'text-red-600';}
    if (percentage >= 80) {return 'text-yellow-600';}
    return 'text-green-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">용량 데이터 로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminHeader
        title="용량 관리"
        subtitle="데이터베이스 및 디스크 사용량을 모니터링하고 예측합니다"
        showRefresh={true}
        onRefresh={handleRefresh}
        isRefreshing={refreshing}
        lastUpdated={lastUpdated}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4"
          >
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          </motion.div>
        )}

        {/* Summary Cards */}
        {capacityData && (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
              >
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Database className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                          데이터베이스 크기
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {capacityData.database.totalSizeFormatted}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
              >
                <div className="p-5">
                  <div className="flex items-center mb-3">
                    <div className="flex-shrink-0">
                      <HardDrive className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        디스크 사용량
                      </h3>
                    </div>
                  </div>
                  
                  {/* 프로그레스 바 */}
                  <div className="mb-3">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                      <span>{capacityData.disk.usedFormatted} / {capacityData.disk.totalFormatted}</span>
                      <span className={`font-medium ${getUsageColor(capacityData.disk.usagePercentage)}`}>
                        {capacityData.disk.usagePercentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          capacityData.disk.usagePercentage >= 90 
                            ? 'bg-red-500' 
                            : capacityData.disk.usagePercentage >= 80 
                            ? 'bg-yellow-500' 
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(capacityData.disk.usagePercentage, 100)}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* 여유 공간 정보 */}
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    여유 공간: <span className="font-medium text-gray-700 dark:text-gray-300">
                      {capacityData.disk.freeFormatted}
                    </span>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
              >
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <TrendingUp className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                          일일 증가율
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {capacityData.growth.dailyGrowthFormatted || '0 MB/day'}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* 용량 예측 모델 */}
            {capacityData.growth.predictions && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 shadow rounded-lg p-6 mb-8"
              >
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-indigo-600" />
                  용량 증가 예측 모델
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">1개월 후</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {capacityData.growth.predictions.oneMonth.sizeFormatted}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {capacityData.growth.predictions.oneMonth.date}
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">3개월 후</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {capacityData.growth.predictions.threeMonths.sizeFormatted}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {capacityData.growth.predictions.threeMonths.date}
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">6개월 후</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {capacityData.growth.predictions.sixMonths.sizeFormatted}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {capacityData.growth.predictions.sixMonths.date}
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">1년 후</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {capacityData.growth.predictions.oneYear.sizeFormatted}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {capacityData.growth.predictions.oneYear.date}
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    💡 현재 일일 증가율 <strong>{capacityData.growth.dailyGrowthFormatted}</strong>을 기준으로 한 선형 예측입니다.
                    실제 증가율은 서비스 이용량에 따라 변동될 수 있습니다.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Detailed Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* 디스크 사용량 상세 정보 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white dark:bg-gray-800 shadow rounded-lg p-6"
              >
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                  <HardDrive className="h-5 w-5 mr-2 text-green-600" />
                  디스크 상세 정보
                </h3>
                
                <div className="space-y-4">
                  {/* 전체 용량 */}
                  <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-400">총 용량</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {capacityData.disk.totalFormatted}
                    </span>
                  </div>
                  
                  {/* 사용된 용량 */}
                  <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-400">사용된 용량</span>
                    <span className={`text-sm font-medium ${getUsageColor(capacityData.disk.usagePercentage)}`}>
                      {capacityData.disk.usedFormatted}
                    </span>
                  </div>
                  
                  {/* 여유 공간 */}
                  <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-400">여유 공간</span>
                    <span className="text-sm font-medium text-green-600">
                      {capacityData.disk.freeFormatted}
                    </span>
                  </div>
                  
                  {/* MongoDB 데이터 비율 */}
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">MongoDB 데이터</span>
                    <span className="text-sm font-medium text-blue-600">
                      {capacityData.database.totalSizeFormatted}
                      <span className="text-xs text-gray-500 ml-1">
                        ({((capacityData.database.totalSize / capacityData.disk.used) * 100).toFixed(1)}%)
                      </span>
                    </span>
                  </div>
                  
                  {/* 상태 인디케이터 */}
                  <div className="mt-4 p-3 rounded-md bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-2 ${
                        capacityData.disk.usagePercentage >= 90 
                          ? 'bg-red-500' 
                          : capacityData.disk.usagePercentage >= 80 
                          ? 'bg-yellow-500' 
                          : 'bg-green-500'
                      }`} />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {capacityData.disk.usagePercentage >= 90 
                          ? '위험: 디스크 공간이 부족합니다' 
                          : capacityData.disk.usagePercentage >= 80 
                          ? '주의: 디스크 공간을 확인하세요' 
                          : '정상: 충분한 디스크 공간'}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* 컬렉션별 상세 정보 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white dark:bg-gray-800 shadow rounded-lg p-6"
              >
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                  <Database className="h-5 w-5 mr-2 text-blue-600" />
                  데이터베이스 구성
                </h3>
                
                <div className="space-y-3">
                  {/* 데이터 vs 인덱스 비율 */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span>데이터 / 인덱스</span>
                      <span>{capacityData.database.dataSizeFormatted} / {capacityData.database.indexSizeFormatted}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div className="flex h-2 rounded-full overflow-hidden">
                        <div 
                          className="bg-blue-500"
                          style={{ 
                            width: `${(capacityData.database.dataSize / capacityData.database.totalSize) * 100}%` 
                          }}
                        />
                        <div 
                          className="bg-purple-500"
                          style={{ 
                            width: `${(capacityData.database.indexSize / capacityData.database.totalSize) * 100}%` 
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span className="flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-1" />
                        데이터
                      </span>
                      <span className="flex items-center">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mr-1" />
                        인덱스
                      </span>
                    </div>
                  </div>
                  
                  {/* Top 5 컬렉션 */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      주요 컬렉션 (크기순)
                    </h4>
                    <div className="space-y-2">
                      {capacityData.database.collections
                        .sort((a, b) => b.size - a.size)
                        .slice(0, 5)
                        .map((collection) => (
                          <div key={collection.name} className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400 truncate">
                              {collection.name}
                            </span>
                            <span className="text-gray-900 dark:text-white font-medium">
                              {collection.sizeFormatted}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* 전체 컬렉션 테이블 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 shadow rounded-lg p-6"
            >
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                컬렉션별 상세 정보
              </h3>
              
              {capacityData.database.collections.length > 0 ? (
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          컬렉션
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          크기
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          문서 수
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          평균 문서 크기
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          작업
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {capacityData.database.collections.map((collection) => {
                        const isProtected = ['users', 'admin_users'].includes(collection.name);
                        const isClearing = clearingCollection === collection.name;
                        
                        return (
                          <tr 
                            key={collection.name} 
                            className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              <div className="flex items-center">
                                <span 
                                  className={`${collection.documents > 0 ? 'cursor-pointer hover:text-blue-600' : ''}`}
                                  onClick={() => {
                                    if (collection.documents > 0) {
                                      setSelectedCollection(collection.name);
                                      setModalOpen(true);
                                    }
                                  }}
                                >
                                  {collection.name}
                                </span>
                                {collection.documents > 0 && (
                                  <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                                    📄 데이터 보기
                                  </span>
                                )}
                                {isProtected && (
                                  <span className="ml-2 text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/20 px-2 py-1 rounded">
                                    🔒 보호됨
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {collection.sizeFormatted}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {collection.documents.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {(collection.avgDocSize / 1024).toFixed(2)} KB
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {!isProtected && collection.documents > 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClearCollection(collection.name);
                                  }}
                                  disabled={isClearing}
                                  className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40"
                                  title={`${collection.name} 컬렉션 초기화`}
                                >
                                  {isClearing ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-1"></div>
                                      삭제 중...
                                    </>
                                  ) : (
                                    <>
                                      <Trash2 className="h-3 w-3 mr-1" />
                                      초기화
                                    </>
                                  )}
                                </button>
                              )}
                              {isProtected && (
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  보호된 컬렉션
                                </span>
                              )}
                              {!isProtected && collection.documents === 0 && (
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  데이터 없음
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">컬렉션 데이터를 불러올 수 없습니다.</p>
              )}
            </motion.div>
          </>
        )}
      </div>

      {/* 컬렉션 데이터 모달 */}
      <CollectionDataModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedCollection('');
        }}
        collectionName={selectedCollection}
      />
    </div>
  );
};

export default CapacityManagementPage;